import type { Agenda, AgendaModule } from "@repo/types";
import { agendaTemplateService } from "@/services/agendaTemplateService";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";
import { agendaModuleRepo } from "@/repositories/agendaModuleRepo";
import { agendaRepo } from "@/repositories/agendaRepo";

export const useCopyAgendaModuleTemplate = (
  tenantId: string,
  keepAsTemplate: boolean = true
) => {
  const trackNew = useTrackedNewObjects();
  return {
    copyAsync: async (agendaModule: AgendaModule, teamId: string) => {
      // copy agenda items
      const agendaItemIds: string[] = [];
      for (const agendaItem of agendaModule.agendaItems ?? []) {
        const copiedAgendaItem = agendaTemplateService.getCopiedAgendaItem(
          agendaItem,
          teamId
        );
        if (!keepAsTemplate) {
          copiedAgendaItem.template = false;
          copiedAgendaItem.templateId = undefined;
        }

        const newAgendaItem = await agendaItemRepo.create(
          tenantId,
          copiedAgendaItem
        );
        if (newAgendaItem) agendaItemIds.push(newAgendaItem.id);
        trackNew("agendaItem", newAgendaItem);
      }

      const copiedAgendaModule = agendaTemplateService.getCopiedAgendaModule(
        agendaModule,
        teamId,
        agendaItemIds
      );
      if (!keepAsTemplate) {
        copiedAgendaModule.template = false;
        copiedAgendaModule.templateId = undefined;
      }

      const newAgendaModule = await agendaModuleRepo.create(
        tenantId,
        copiedAgendaModule
      );
      trackNew("agendaModule", newAgendaModule);
      return newAgendaModule;
    },
  };
};

export const useCopyAgendaTemplate = (tenantId: string) => {
  const copyAgendaModuleTemplate = useCopyAgendaModuleTemplate(tenantId);
  const trackNew = useTrackedNewObjects();
  return {
    copyAsync: async (agenda: Agenda, teamId: string) => {
      const agendaModules = agenda?.agendaModules ?? [];
      const agendaModuleIds: string[] = [];
      for (const agendaModule of agendaModules) {
        const newAgendaModule = await copyAgendaModuleTemplate.copyAsync(
          agendaModule,
          teamId
        );
        if (newAgendaModule) {
          agendaModuleIds.push(newAgendaModule.id);
        }
      }
      const copiedAgenda = agendaTemplateService.getCopiedAgenda(
        agenda,
        teamId,
        agendaModuleIds
      );
      const newAgenda = await agendaRepo.create(teamId, copiedAgenda);
      trackNew("agenda", newAgenda);
      return newAgenda?.id;
    },
  };
};
